import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  InputBase,
  IconButton,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PaymentsIcon from "@mui/icons-material/Payments";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExtensionIcon from "@mui/icons-material/Extension";
import InsightsIcon from "@mui/icons-material/Insights";
/* --------------------------------------------------
   SIDEBAR
-------------------------------------------------- */
const Sidebar = ({ mobileOpen, onDrawerToggle, onPanelToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [panelOpen, setPanelOpen] = useState(false);
  const drawerWidth = panelOpen ? 320 : 100;

  /* ---------------- MENU ---------------- */
  const parentMenu = [
    { label: "Dashboard", icon: <DashboardIcon />, path: "/parent" },
    {
      label: "Attendance",
      icon: <EventAvailableIcon />,
      path: "/parent/attendance",
    },
    {
      label: "Activities",
      icon: <ExtensionIcon />,
      path: "/parent/activities",
    },
    { label: "Photos", icon: <PhotoLibraryIcon />, path: "/parent/gallery" },
    { label: "Progress", icon: <InsightsIcon />, path: "/parent/progress" },
    { label: "Fees", icon: <PaymentsIcon />, path: "/parent/fees" },
    {
      label: "Announcements",
      icon: <NotificationsIcon />,
      path: "/parent/announcements",
    },
    {
      label: "Calendar",
      icon: <CalendarMonthIcon />,
      path: "/parent/calendar",
    },
    // { label: "Report Card", icon: <SchoolIcon />, path: "/parent/report-card" },
    // { label: "Feedback", icon: <StarIcon />, path: "/parent/feedback" },
  ];

  const menuItems = parentMenu;

  const [selectedNav, setSelectedNav] = useState(menuItems[0].label);

  /* auto select on route change */
  useEffect(() => {
    const active = menuItems.find((m) => m.path === location.pathname);
    if (active) setSelectedNav(active.label);
  }, [location.pathname]);

  const handleNavClick = (label) => {
    setSelectedNav(label);
  };

  const handlePanelIconClick = () => {
    const newPanelState = !panelOpen;
    setPanelOpen(newPanelState);
    if (onPanelToggle) {
      onPanelToggle(newPanelState);
    }
  };

  const handleDrawerClose = () => {
    if (onDrawerToggle) onDrawerToggle();
    setPanelOpen(false);
    if (onPanelToggle) onPanelToggle(false);
  };

  const handleLogout = () => {
    setPanelOpen(false);
    if (onPanelToggle) onPanelToggle(false);
    if (onDrawerToggle) onDrawerToggle(false);

    localStorage.clear();
    navigate("/");
  };

  /* ---------------- DRAWER CONTENT ---------------- */
  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "row",
        p: { xs: 1.5, sm: 2 },
        pr: 0,
        boxSizing: "border-box",
        bgcolor: "transparent",
        alignItems: "stretch",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {/* LEFT ICON STRIP */}
      <Box
        sx={{
          width: { xs: 52, sm: 56 },
          borderRadius: 999,
          bgcolor: "#fff",
          boxShadow: "0 18px 40px rgba(15,23,42,0.25)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: { xs: 1.5, sm: 2 },
          gap: 1.8,
          height: "100%",
          flexShrink: 0,
        }}
      >
        {menuItems.map((item) => {
          const active = selectedNav === item.label;

          return (
            <IconButton
              key={item.label}
              onClick={() => {
                setSelectedNav(item.label);
                navigate(item.path);
                setPanelOpen(true);
                if (onPanelToggle) onPanelToggle(true);
              }}
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: active ? "rgba(99,102,241,0.18)" : "transparent",
                boxShadow: active ? "0 0 10px rgba(99,102,241,0.6)" : "none",
                color: active ? "#4F46E5" : "#6B7280",
                transition: "all .25s ease",
                "&:hover": {
                  bgcolor: "rgba(99,102,241,0.14)",
                  transform: "scale(1.05)",
                },
              }}
            >
              {item.icon}
            </IconButton>
          );
        })}

        <Box sx={{ flexGrow: 1 }} />
      </Box>

      {/* RIGHT DARK PANEL */}
      <Box
        sx={{
          ml: panelOpen ? { xs: 1.5, sm: 2 } : 0,
          flexGrow: panelOpen ? 1 : 0,
          width: panelOpen ? "auto" : 0,
          opacity: panelOpen ? 1 : 0,
          pointerEvents: panelOpen ? "auto" : "none",
          width: panelOpen ? { xs: 240, md: 320 } : 0, // FIXED WIDTH
          transform: panelOpen ? "translateX(0)" : "translateX(-16px)",
          transition: "all 0.25s ease, opacity 0.2s ease, transform 0.25s ease",
          borderRadius: { xs: 3, sm: 4 },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: panelOpen ? "0 24px 80px rgba(15,23,42,0.65)" : "none",
          background:
            "radial-gradient(120% 160% at 0% 0%, #111827 0%, #020617 45%, #020617 100%)",
          color: "#F9FAFB",
          alignSelf: "stretch",
          minWidth: 0,
        }}
      >
        {panelOpen && (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              px: { xs: 2, sm: 2.5 },
              pt: { xs: 2, sm: 2.5 },
              pb: 2,
              boxSizing: "border-box",
              height: "100%",
              minWidth: 0,
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                pb: 1.5,
                borderBottom: "1px solid rgba(148,163,184,0.18)",
                gap: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "18px", sm: "20px" },
                  letterSpacing: "0.5px",
                  background:
                    "linear-gradient(90deg, #FFFFFF 0%, #94A3B8 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {selectedNav}
              </Typography>

              <IconButton
                size="small"
                onClick={() => {
                  setPanelOpen(false);
                  if (onPanelToggle) onPanelToggle(false);
                  if (onDrawerToggle) onDrawerToggle(false); // CLOSE FULL SIDEBAR
                }}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "999px",
                  bgcolor: "rgba(15,23,42,0.9)",
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(15,23,42,1)" },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* SEARCH */}
            <Box sx={{ mb: 3.5, position: "relative" }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "rgba(148,163,184,0.9)",
                  mb: 1,
                  ml: 0.5,
                  letterSpacing: "0.5px",
                }}
              >
                Quick Search
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "rgba(15,23,42,0.75)",
                  borderRadius: "12px",
                  px: 2,
                  py: 1.2,
                  border: "1px solid rgba(99,102,241,0.25)",
                  transition: "all 0.3s ease",
                  "&:focus-within": {
                    borderColor: "rgba(99,102,241,0.6)",
                    boxShadow: "0 0 0 3px rgba(99,102,241,0.16)",
                    bgcolor: "rgba(15,23,42,0.9)",
                  },
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: "#818CF8", mr: 1.5 }} />
                <InputBase
                  placeholder="Search..."
                  sx={{ color: "#E5E7EB", fontSize: 14, flex: 1 }}
                />
              </Box>
            </Box>

            {/* NAVIGATION */}
            <List dense disablePadding sx={{ flexGrow: 1 }}>
              {menuItems.map((item) => (
                <ListItemButton
                  key={item.label}
                  selected={selectedNav === item.label}
                  onClick={() => {
                    setSelectedNav(item.label);
                    navigate(item.path);
                    setPanelOpen(false);
                    if (onPanelToggle) onPanelToggle(false);
                    if (onDrawerToggle) onDrawerToggle(false);
                  }}
                  sx={{
                    borderRadius: "10px",
                    px: 2,
                    py: 1,
                    mb: 1,
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    "& .MuiListItemIcon-root": {
                      minWidth: 36,
                    },
                    "&.Mui-selected": {
                      bgcolor: "rgba(99,102,241,0.22)",
                      borderLeft: "3px solid #818CF8",
                      transform: "translateX(2px)",
                      "& .MuiListItemIcon-root": {
                        color: "#818CF8",
                      },
                      "& .MuiListItemText-primary": {
                        fontWeight: 600,
                        color: "#FFFFFF",
                      },
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: "rgba(99,102,241,0.3)",
                    },
                    "&:hover": {
                      bgcolor: "rgba(148,163,184,0.16)",
                      transform: "translateX(4px)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: selectedNav === item.label ? "#818CF8" : "#9CA3AF",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: selectedNav === item.label ? 600 : 400,
                      letterSpacing: "0.3px",
                      color: selectedNav === item.label ? "#FFFFFF" : "#E5E7EB",
                    }}
                  />
                  {selectedNav === item.label && (
                    <KeyboardArrowRightIcon sx={{ color: "#818CF8" }} />
                  )}
                </ListItemButton>
              ))}
            </List>

            {/* LOGOUT */}
            {/* <Divider sx={{ my: 2, borderColor: "rgba(148,163,184,0.18)" }} /> */}
            <Box
              sx={{
                mt: "auto",
                pt: 2,
                borderTop: "1px solid rgba(148,163,184,0.18)",
              }}
            >
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: "10px",
                  px: 2,
                  py: 1.2,
                  bgcolor: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    bgcolor: "rgba(239,68,68,0.16)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(239,68,68,0.18)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "#FCA5A5" }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#FCA5A5",
                    letterSpacing: "0.3px",
                  }}
                />
              </ListItemButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  /* ---------------- DRAWERS ---------------- */
  return (
    <>
      {/* MOBILE */}
      <Drawer
        anchor="left"
        variant="temporary"
        open={mobileOpen}
        onClose={(_, reason) => {
          // close when clicking outside or pressing Esc
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            if (onDrawerToggle) onDrawerToggle(false); // close the drawer
            setPanelOpen(false); // also close dark panel
          }
        }}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true, // ✅ ADD HERE
        }}
        sx={{ display: { xs: "block", md: "none" } }}
        PaperProps={{
          sx: {
            boxSizing: "border-box",
            width: "auto",
            border: "none",
            bgcolor: "transparent",
            overflow: "visible",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* DESKTOP */}
      <Drawer
        variant="permanent"
        open
        ModalProps={{
          disableScrollLock: true, // ✅ ADD HERE (safe even if ignored)
        }}
        sx={{
          display: { xs: "none", md: "block" },
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            maxWidth: drawerWidth,
            border: "none",
            bgcolor: "transparent",
            overflow: "hidden",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;
